// Utility functions to handle subdomain change events

/**
 * Dispatches an event when the subdomain changes
 * @param {string} newSubdomain - The new subdomain value
 * @param {string} previousSubdomain - The previous subdomain value
 * @param {Object} options - Additional options
 * @param {boolean} options.preventNavigation - If true, navigation will not be triggered
 * @param {string} options.currentPath - Current path (for path preservation)
 */
export const dispatchSubdomainChanged = (newSubdomain, previousSubdomain, options = {}) => {
  const { preventNavigation = false, currentPath = null } = options;
  
  const event = new CustomEvent('subdomain-changed', {
    detail: {
      newSubdomain,
      previousSubdomain,
      timestamp: new Date().getTime(),
      preventNavigation,
      currentPath
    }
  });
  
  // Store the change in sessionStorage for components that load after the event
  sessionStorage.setItem('subdomain_change', JSON.stringify({
    newSubdomain,
    previousSubdomain,
    timestamp: new Date().getTime(),
    preventNavigation,
    currentPath
  }));
  
  // Set flag to indicate this is a subdomain switch, not a navigation away from subdomain
  sessionStorage.setItem('isSubdomainSwitching', 'true');
  
  console.log(`[subdomainEvents] Dispatching subdomain change: ${previousSubdomain} -> ${newSubdomain}`, 
              preventNavigation ? '(navigation prevented)' : '');
  window.dispatchEvent(event);
};

/**
 * Checks if there was a recent subdomain change
 * @param {number} maxAgeMs - Maximum age in milliseconds to consider the change "recent"
 * @returns {Object|null} - The subdomain change info or null if none/not recent
 */
export const getRecentSubdomainChange = (maxAgeMs = 10000) => {
  const changeData = sessionStorage.getItem('subdomain_change');
  if (!changeData) return null;
  
  try {
    const change = JSON.parse(changeData);
    const now = new Date().getTime();
    
    if (now - change.timestamp <= maxAgeMs) {
      return change;
    }
    
    return null;
  } catch (err) {
    console.error('[subdomainEvents] Error parsing subdomain change data:', err);
    return null;
  }
};

/**
 * Clears the stored subdomain change data
 */
export const clearSubdomainChangeData = () => {
  sessionStorage.removeItem('subdomain_change');
}; 